<script setup>
    import { computed, ref, defineProps } from 'vue';
    
    const props = defineProps({
        data: {
            type: Array,
            required: true,
            default: () => []
        },
        columns: {
            type: Array,
            required: true,
            default: () => []
        },
        searchPlaceholder: {
            type: String,
            default: 'Search...'
        },
        searchable: {
            type: Boolean,
            default: true
        }
    });
    
    const searchQuery = ref('');
    const currentPage = ref(1);
    const itemsPerPage = ref(10);
    
    const getDefaultSortColumn = () => {
        const averageColumn = props.columns.find(col => col.key === 'average');
        if (averageColumn) return 'average';
        return null;
    };
    
    const sortBy = ref(getDefaultSortColumn());
    const sortOrder = ref('desc');
    
    const itemsPerPageOptions = [
        { label: '10', value: 10 },
        { label: '25', value: 25 },
        { label: '50', value: 50 },
        { label: '100', value: 100 }
    ];
    
    const handleSort = (column) => {
        if (!column.sortable) return;
        
        if (sortBy.value === column.key) {
            sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
        } else {
            sortBy.value = column.key;
            sortOrder.value = 'asc';
        }
    };
    
    const filteredData = computed(() => {
        if (!searchQuery.value || !props.searchable) return props.data;
        
        const query = searchQuery.value.toLowerCase();
        return props.data.filter(item => {
            return props.columns.some(column => {
                const value = item[column.key];
                if (value === null || value === undefined) return false;
                return value.toString().toLowerCase().includes(query);
            });
        });
    });
    
    const filteredAndSortedData = computed(() => {
        let result = [...filteredData.value];
        
        if (sortBy.value) {
            result.sort((a, b) => {
                let aValue = a[sortBy.value];
                let bValue = b[sortBy.value];
                
                if (aValue === null || aValue === undefined) return 1;
                if (bValue === null || bValue === undefined) return -1;
                
                const column = props.columns.find(col => col.key === sortBy.value);
                const columnType = column?.type || 'text';
                
                if (columnType === 'date') {
                    const aDate = new Date(aValue).getTime();
                    const bDate = new Date(bValue).getTime();
                    if (Number.isNaN(aDate)) return 1;
                    if (Number.isNaN(bDate)) return -1;
                    const diff = aDate - bDate;
                    return sortOrder.value === 'asc' ? diff : -diff;
                }
                
                if (columnType === 'number') {
                    const aNum = Number(aValue);
                    const bNum = Number(bValue);
                    if (Number.isNaN(aNum)) return 1;
                    if (Number.isNaN(bNum)) return -1;
                    const diff = aNum - bNum;
                    return sortOrder.value === 'asc' ? diff : -diff;
                }
                
                const aStr = String(aValue).toLowerCase();
                const bStr = String(bValue).toLowerCase();
                const comparison = aStr.localeCompare(bStr, undefined, { sensitivity: 'base', numeric: true });
                return sortOrder.value === 'asc' ? comparison : -comparison;
            });
        }
        
        return result;
    });
    
    const totalItems = computed(() => filteredAndSortedData.value.length);
    const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value));
    const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);
    const endIndex = computed(() => startIndex.value + itemsPerPage.value);
    const paginatedData = computed(() => {
        return filteredAndSortedData.value.slice(startIndex.value, endIndex.value);
    });
    
    const displayedPages = computed(() => {
        const current = currentPage.value;
        const total = totalPages.value;
        
        if (total <= 3) {
            return Array.from({length: total}, (_, i) => i + 1);
        }
        
        const pages = [];
        
        if (current > 2) {
            pages.push(1);
            if (current > 3) pages.push('...');
        }
        
        if (current > 1) pages.push(current - 1);
        pages.push(current);
        if (current < total) pages.push(current + 1);
        
        if (current < total - 1) {
            if (current < total - 2) pages.push('...');
            pages.push(total);
        }
        
        return pages;
    });
    
    const handleItemsPerPageChange = (value) => {
        itemsPerPage.value = Number(value);
        
        if (currentPage.value > totalPages.value) {
            currentPage.value = totalPages.value || 1;
        }
    };
    
    const prevPage = () => {
        if (currentPage.value > 1) {
            currentPage.value--;
        }
    };
    
    const nextPage = () => {
        if (currentPage.value < totalPages.value) {
            currentPage.value++;
        }
    };
    
    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages.value) {
            currentPage.value = page;
        }
    };
    
    const getCellValue = (item, column) => {
        const value = item[column.key];
        
        if (column.formatter && typeof column.formatter === 'function') {
            return column.formatter(value, item);
        }
        
        return value;
    };
    </script>
    
    <template>
        <div class="w-full h-full">
            <!-- Table View -->
            <div v-if="data.length > 0" class="block">
                <!-- Pagination and Search Section -->
                <div class="flex items-center px-2 py-1.5 sm:py-2 bg-[#0F0F0F] border-y border-echoes-border-gray">
                    <div class="flex  flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3 md:gap-4 w-full">
                        <!-- Info + Items per page (esquerda) -->
                        <div class="flex flex-row items-center gap-2 sm:gap-3 md:gap-4 px-2">
                            <div class="text-[10px] sm:text-xs text-white/80 whitespace-nowrap"> 
                                Showing {{ startIndex + 1 }}-{{ Math.min(endIndex, totalItems) }} of {{ totalItems }} items
                            </div>
                            <div class="flex items-center gap-1.5 sm:gap-2 border-l border-[#0F0F0F] pl-2 sm:pl-3 md:pl-4">
                                <label for="itemsPerPage" class="text-[10px] sm:text-xs text-white/80 whitespace-nowrap">Items per page:</label>
                                <select
                                    id="itemsPerPage"
                                    :value="itemsPerPage"
                                    @change="handleItemsPerPageChange($event.target.value)"
                                    class="bg-echoes-background-black border border-echoes-border-gray text-[10px] sm:text-xs rounded-md sm:rounded-lg focus:ring-echoes-primary focus:border-echoes-primary p-0.5 sm:p-1 md:p-1.5 dark:text-white appearance-none text-center min-w-fit"
                                >
                                    <option
                                        v-for="option in itemsPerPageOptions"
                                        :key="option.value"
                                        :value="option.value"
                                    >
                                        {{ option.label }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <!-- Search -->
                        <div v-if="searchable" class="flex items-center w-full sm:w-auto">
                            <div class="relative w-full sm:w-auto">
                                <input
                                    v-model="searchQuery"
                                    :placeholder="searchPlaceholder"
                                    type="text"
                                    class="w-full h-[30px] bg-echoes-background-black border border-echoes-border-gray text-[10px] text-xs rounded-md sm:rounded-lg focus:ring-echoes-primary focus:border-echoes-primary py-1 sm:py-1.5 md:py-2 px-2 sm:px-3 md:px-4 text-white/90 placeholder-white/40"
                                />
                            </div>
                        </div>
                    </div>
                </div>
    
                <!-- Table -->
                <div class="overflow-x-auto">
                    <table class="w-full border-collapse text-sm">
                        <thead class="bg-echoes-background-black">
                            <tr class="border-b border-[#0F0F0F]">
                                <th 
                                    v-for="column in columns" 
                                    :key="column.key"
                                    @click="handleSort(column)"
                                    class="px-4 py-2 text-left text-xs font-medium text-white/80 uppercase tracking-wider whitespace-nowrap"
                                    :class="[
                                        column.headerClass,
                                        column.sortable ? 'cursor-pointer hover:text-white select-none' : ''
                                    ]"
                                >
                                    <div class="flex items-center gap-2">
                                        <span>{{ column.label }}</span>
                                        <span v-if="column.sortable" class="flex flex-col">
                                            <i 
                                                v-if="sortBy === column.key && sortOrder === 'asc'"
                                                class="mdi mdi-arrow-up text-echoes-primary"
                                            ></i>
                                            <i 
                                                v-else-if="sortBy === column.key && sortOrder === 'desc'"
                                                class="mdi mdi-arrow-down text-echoes-primary"
                                            ></i>
                                            <i 
                                                v-else
                                                class="mdi mdi-unfold-more-horizontal text-white/40"
                                            ></i>
                                        </span>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-transparent divide-y divide-[#0F0F0F]">
                            <tr v-for="(item, index) in paginatedData" :key="index"
                                class="hover:bg-echoes-background-black/30 transition-colors duration-200">
                                <td 
                                    v-for="column in columns" 
                                    :key="column.key"
                                    class="px-4 py-4 text-sm text-white/80"
                                    :class="column.cellClass"
                                >
                                    <slot 
                                        :name="`cell-${column.key}`" 
                                        :item="item" 
                                        :value="item[column.key]"
                                    >
                                        <span :class="column.valueClass">
                                            {{ getCellValue(item, column) }}
                                        </span>
                                    </slot>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
    
                <!-- Pagination Footer -->
                <div class="flex flex-col sm:flex-row items-center justify-center px-4 bg-[#0F0F0F] rounded-b-[10px] border-t border-echoes-border-gray">
                    <!-- Navigation -->
                    <div class="flex items-center gap-2">
                        <button @click="prevPage" :disabled="currentPage === 1" 
                            class="text-sm rounded-md transition-colors duration-200 p-1.5"
                            :class="[
                                currentPage === 1
                                    ? 'text-white/40 cursor-not-allowed'
                                    : 'text-white/80 hover:bg-echoes-background-black/30'
                            ]">
                            <i class="mdi mdi-chevron-left text-lg"></i>
                        </button>
                        
                        <div class="flex items-center gap-1">
                            <template v-for="page in displayedPages" :key="page">
                                <span v-if="page === '...'" class="px-2 text-sm text-white/60">...</span>
                                <button v-else @click="goToPage(page)"
                                    class="px-2 py-1 text-xs rounded-md transition-colors duration-200 min-w-[28px]"
                                    :class="[
                                        currentPage === page
                                            ? 'bg-echoes-primary text-white hover:bg-echoes-primary/80'
                                            : 'text-white/80 hover:bg-echoes-background-black/30'
                                    ]">
                                    {{ page }}
                                </button>
                            </template>
                        </div>
        
                        <button @click="nextPage" :disabled="currentPage === totalPages"
                            class="text-xs rounded-md transition-colors duration-200 p-1"
                            :class="[
                                currentPage === totalPages
                                    ? 'text-white/40 cursor-not-allowed'
                                    : 'text-white/80 hover:bg-echoes-background-black/30'
                            ]">
                            <i class="mdi mdi-chevron-right text-lg"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </template>
    
    <style scoped lang="postcss">
    /* Add any additional styles if needed */
    </style>
    
    